import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { QUERY_KEYS } from "@/services/api/queryKeys";
import { MUTATION_KEYS } from "@/services/api/mutationKeys";
import type { TenantDomain, AddDomainPayload } from "@/types";

export const usePortalDomains = (portalId: string) => {
  const queryClient = useQueryClient();

  // Query: list domains for this portal
  const domainsQuery = useQuery({
    queryKey: QUERY_KEYS.DOMAINS.LIST(portalId),
    queryFn: async () => {
      const response = await apiClient.get<
        TenantDomain[] | { domains: TenantDomain[] }
      >(API_ENDPOINTS.SYSTEM.DOMAINS.LIST(portalId));
      const data = response.data;
      return Array.isArray(data) ? data : data.domains;
    },
    enabled: !!portalId,
    // Auto-poll every 30s when any domain is DEPLOYING
    refetchInterval: (query) => {
      const domains = query.state.data;
      if (domains?.some((d) => d.status === "DEPLOYING")) {
        return 30_000;
      }
      return false;
    },
  });

  // Mutation: add a new custom domain
  const addDomainMutation = useMutation({
    mutationKey: [MUTATION_KEYS.ADD_DOMAIN],
    mutationFn: async (payload: AddDomainPayload) => {
      const response = await apiClient.post<TenantDomain>(
        API_ENDPOINTS.SYSTEM.DOMAINS.ADD(portalId),
        payload
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DOMAINS.ALL(portalId),
      });
    },
  });

  // Mutation: trigger verification for a domain
  const verifyDomainMutation = useMutation({
    mutationKey: [MUTATION_KEYS.VERIFY_DOMAIN],
    mutationFn: async (domainId: string) => {
      const response = await apiClient.post<TenantDomain>(
        API_ENDPOINTS.SYSTEM.DOMAINS.VERIFY(portalId, domainId)
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DOMAINS.ALL(portalId),
      });
    },
  });

  // Mutation: remove a domain
  const removeDomainMutation = useMutation({
    mutationKey: [MUTATION_KEYS.REMOVE_DOMAIN],
    mutationFn: async (domainId: string) => {
      await apiClient.delete(
        API_ENDPOINTS.SYSTEM.DOMAINS.REMOVE(portalId, domainId)
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.DOMAINS.ALL(portalId),
      });
    },
  });

  return {
    domains: domainsQuery.data ?? [],
    isLoading: domainsQuery.isLoading,
    error: domainsQuery.error,

    addDomain: addDomainMutation.mutate,
    isAdding: addDomainMutation.isPending,

    verifyDomain: verifyDomainMutation.mutate,
    isVerifying: verifyDomainMutation.isPending,

    removeDomain: removeDomainMutation.mutate,
    isRemoving: removeDomainMutation.isPending,
  };
};
